import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCartIcon, MagnifyingGlassIcon, UserCircleIcon, Bars3Icon, XMarkIcon, HeartIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import LoginModal from './LoginModal';
import SearchModal from './SearchModal';

const Navbar: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const { items, toggleCart } = useCart();
  const { wishlistIds } = useWishlist();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { name: 'SAMPLE PACKS', href: '/samples' },
    { name: 'PRESETS', href: '/presets' },
    { name: 'TEMPLATES', href: '/templates' },
    { name: 'BUNDLES', href: '/bundles' },
    { name: 'BEST SELLERS', href: '/best-sellers' },
    { name: 'CONTACT', href: '/contact' },
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/5">
        <div className="w-full">
          <div className="flex items-center justify-between h-16 px-2 sm:px-4 lg:px-8">
            {/* Logo */}
            <div className="flex-shrink-0 min-w-0">
              <Link to="/" className="flex items-center">
                <span className="font-alivegraff text-sm sm:text-xl lg:text-2xl tracking-[0.18em] sm:tracking-wider text-white uppercase leading-none whitespace-nowrap hover:text-neon-red transition-colors duration-300 drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
                  STREETS AUDIO
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`nav-link pb-4 whitespace-nowrap text-xs lg:text-sm ${location.pathname === item.href ? 'nav-link-active' : 'nav-link-inactive'}`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Mobile menu button */}
              <button
                type="button"
                className="lg:hidden p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5"
                onClick={() => setMobileMenuOpen((open) => !open)}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
              </button>
              <button
                type="button"
                className="nav-link-inactive p-1 rounded-full hover:bg-white/5 lg:inline-flex hidden"
                onClick={() => setIsSearchModalOpen(true)}
                aria-label="Search"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>
              <Link
                to="/wishlist"
                className="nav-link-inactive p-1 rounded-full hover:bg-white/5 relative"
                aria-label={wishlistIds.length > 0 ? `Wishlist (${wishlistIds.length} items)` : 'Wishlist'}
              >
                <HeartIcon className="h-5 w-5" />
                {wishlistIds.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-neon-red text-white text-xs w-4 h-4 flex items-center justify-center rounded-full">
                    {wishlistIds.length}
                  </span>
                )}
              </Link>
              <button
                type="button"
                className="nav-link-inactive p-1 rounded-full hover:bg-white/5 relative"
                onClick={toggleCart}
                aria-label={items.length > 0 ? `Cart (${items.length} items)` : 'Open cart'}
              >
                <ShoppingCartIcon className="h-5 w-5" />
                {items.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-neon-red text-white text-xs w-4 h-4 flex items-center justify-center rounded-full">
                    {items.length}
                  </span>
                )}
              </button>
              {/* Mobile-only: account icon (swapped with cart position) */}
              {isAuthenticated ? (
                <Link
                  to="/account"
                  className="nav-link-inactive p-1 rounded-full hover:bg-white/5 inline-flex lg:hidden"
                  aria-label="Account"
                >
                  <UserCircleIcon className="h-5 w-5" />
                </Link>
              ) : (
                <button
                  type="button"
                  className="nav-link-inactive p-1 rounded-full hover:bg-white/5 inline-flex lg:hidden"
                  onClick={() => setIsLoginModalOpen(true)}
                  aria-label="Login"
                >
                  <UserCircleIcon className="h-5 w-5" />
                </button>
              )}

              {isAuthenticated ? (
                <div className="relative group hidden lg:block">
                  <Link to="/account" className="flex items-center space-x-2 nav-link nav-link-inactive hover:text-neon-red">
                    <UserCircleIcon className="h-5 w-5" />
                    <span className="hidden sm:inline">{user?.name}</span>
                  </Link>
                  <div className="absolute right-0 mt-2 w-48 bg-dark-lighter rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="py-1">
                      <Link
                        to="/account"
                        className="block px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                      >
                        My Account
                      </Link>
                      <button
                        onClick={logout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-white/5 transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="nav-link nav-link-inactive hover:text-neon-red hidden lg:inline"
                >
                  LOGIN
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu panel */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-200 ease-out ${
            mobileMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-4 sm:px-6 py-4 border-t border-white/5 bg-dark-lighter space-y-1">
            <button
              type="button"
              onClick={() => {
                setIsSearchModalOpen(true);
                setMobileMenuOpen(false);
              }}
              className="block w-full text-left py-3 px-3 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5"
            >
              Search
            </button>
            <Link
              to="/wishlist"
              className={`block py-3 px-3 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === '/wishlist' ? 'text-white bg-white/10' : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              Wishlist
            </Link>
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block py-3 px-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === item.href
                    ? 'text-white bg-white/10'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {item.name}
              </Link>
            ))}
            {isAuthenticated && (
              <>
                <Link
                  to="/account"
                  className="block py-3 px-3 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5"
                >
                  My Account
                </Link>
                <button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="block w-full text-left py-3 px-3 rounded-lg text-sm font-medium text-red-500 hover:bg-white/5"
                >
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />
    </>
  );
};

export default Navbar; 